import React from 'react';
import Image from 'next/image';
import { useThemeContext } from 'contexts/theme';
import NoImage from 'components/image-loader/image/no-image';
import { isExpTenant } from '@tenants';
import { isActive } from 'types/reso';
import { StandardStatus } from '@zoocasa/go-search';
import type { PriceHistoryTimelineRecordView } from './use-price-history-timeline';
import {
  usePriceHistoryRecordNavigation,
  type PriceHistoryViewListingHandler,
} from './price-history-navigation';
import styles from './price-history-timeline.module.scss';

// Thumbnail renderer for either the listing photo or the no-image placeholder.
function PriceHistoryThumbnail({ record }: { record: PriceHistoryTimelineRecordView }) {
  const { tenantId } = useThemeContext();

  if (record.thumbnailSrc) {
    return (
      <Image
        src={record.thumbnailSrc}
        alt={record.thumbnailAlt}
        className={styles.thumbnail}
        width={72}
        height={72}
      />
    );
  }

  return (
    <div className={styles.placeholder} aria-hidden="true">
      <NoImage isExpTheme={isExpTenant(tenantId)} />
    </div>
  );
}

function getStatusToneClass(status: StandardStatus): string {
  if (status === StandardStatus.STANDARD_STATUS_CLOSED) return styles.sold;
  if (isActive(status)) return styles.active;
  return styles.neutral;
}

export interface PriceHistoryCardProps {
  record: PriceHistoryTimelineRecordView;
  onViewListingClick?: PriceHistoryViewListingHandler;
}

// Full-size card for the newest record in a group, including chip, price, MLS, and lifecycle text.
export function PriceHistoryCard({ record, onViewListingClick }: PriceHistoryCardProps) {
  const statusShortLabel = record.statusLabel;

  const { isClickable, handleClick } = usePriceHistoryRecordNavigation(record, onViewListingClick);

  return (
    <article
      className={`${styles.entry} ${record.isCurrentListing ? styles.currentEntry : ''} ${isClickable ? styles.clickableEntry : ''}`}
      onClick={handleClick}
      onKeyDown={event => {
        if (!isClickable) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <PriceHistoryThumbnail record={record} />
      <div className={styles.entryContent}>
        <div className={styles.leftColumn}>
          <span className={`${styles.statusChip} ${getStatusToneClass(record.status)}`}>
            {statusShortLabel} · {record.daysOnMarketLabel}
          </span>
          <p className={styles.price}>
            {record.priceLabel}
            {record.originalPriceLabel && (
              <span className={styles.originalPrice}>{record.originalPriceLabel}</span>
            )}
          </p>
          {record.mlsLabel && <p className={styles.mls}>{record.mlsLabel}</p>}
        </div>
        <div className={styles.rightColumn}>
          <p className={styles.dateSecondary}>{record.relativeDateLabel ?? '--'}</p>
          <p className={styles.lifecycle}>{record.lifecycleLabel}</p>
        </div>
      </div>
    </article>
  );
}
